import { useState, useEffect } from 'react'
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom'
import {
    Leaf, BarChart3, Camera, DollarSign, MessageSquare,
    CloudRain, LogOut, Menu, X, Bell, User, ChevronDown, ExternalLink, Mail
} from 'lucide-react'
import CropSelector from '../components/dashboard/CropSelector'
import SoilDashboard from '../components/soil/SoilDashboard'
import EnvironmentDashboard from '../components/environment/EnvironmentDashboard'
import VisionDashboard from '../components/vision/VisionDashboard'
import ProductivityDashboard from '../components/productivity/ProductivityDashboard'
import FinanceDashboard from '../components/finance/FinanceDashboard'
import ChatBot from '../components/chatbot/ChatBot'
import { generateAllData, CROP_CONFIG } from '../utils/mockDataGenerator'
import './Dashboard.css'

function Dashboard({ user, onLogout }) {
    const [selectedCrop, setSelectedCrop] = useState(null)
    // Start with sidebar closed on mobile, open on desktop
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768)
    const [showChat, setShowChat] = useState(false)
    const navigate = useNavigate()

    const menuItems = [
        { path: '/dashboard', icon: <Leaf />, label: 'Resumen', exact: true },
        { path: '/dashboard/suelo', icon: <BarChart3 />, label: 'Monitoreo Suelo' },
        { path: '/dashboard/ambiente', icon: <CloudRain />, label: 'Ambiente' },
        { path: '/dashboard/vision', icon: <Camera />, label: 'Análisis IA' },
        { path: '/dashboard/productividad', icon: <BarChart3 />, label: 'Productividad' },
        { path: '/dashboard/finanzas', icon: <DollarSign />, label: 'Finanzas' },
    ]

    const handleLogout = () => {
        onLogout()
        window.location.href = '/index.html'
    }

    return (
        <div className={`dashboard-layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <Leaf size={28} className="logo-icon" />
                        <span className="logo-text">TerraMind</span>
                    </div>
                    <button
                        className="sidebar-toggle"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <div className="sidebar-crop">
                    <CropSelector
                        selectedCrop={selectedCrop}
                        onSelectCrop={(crop) => {
                            setSelectedCrop(crop)
                            // Close sidebar on mobile after selection
                            if (window.innerWidth <= 768) {
                                setSidebarOpen(false)
                            }
                        }}
                    />
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.exact}
                            className={({ isActive }) =>
                                `nav-item ${isActive ? 'active' : ''}`
                            }
                            onClick={() => {
                                // Close sidebar on mobile after navigation
                                if (window.innerWidth <= 768) {
                                    setSidebarOpen(false)
                                }
                            }}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button className="nav-item" onClick={handleLogout}>
                        <LogOut size={20} />
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="dashboard-main">
                {/* Top Bar */}
                <header className="dashboard-topbar">
                    <div className="topbar-left">
                        <button
                            className="mobile-menu-btn"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <Menu size={24} />
                        </button>
                        <h1 className="page-title">
                            {selectedCrop ? `Cultivo: ${selectedCrop}` : 'Selecciona un Cultivo'}
                        </h1>
                    </div>
                    <div className="topbar-right">
                        <button className="topbar-btn">
                            <Bell size={20} />
                            <span className="notification-badge">3</span>
                        </button>
                        <div className="user-menu">
                            <User size={20} />
                            <span>{user?.nombre || 'Usuario'}</span>
                            <ChevronDown size={16} />
                        </div>
                    </div>
                </header>

                {/* Content Area */}
                <div className="dashboard-content">
                    {!selectedCrop ? (
                        <div className="select-crop-message">
                            <Leaf size={64} />
                            <h2>¡Bienvenido a TerraMind!</h2>
                            <p>Selecciona un cultivo en el menú lateral para comenzar a ver los dashboards.</p>
                        </div>
                    ) : (
                        <Routes>
                            <Route path="/" element={
                                <DashboardOverview crop={selectedCrop} />
                            } />
                            <Route path="/suelo" element={
                                <SoilDashboard crop={selectedCrop} />
                            } />
                            <Route path="/ambiente" element={
                                <EnvironmentDashboard crop={selectedCrop} />
                            } />
                            <Route path="/vision" element={
                                <VisionDashboard crop={selectedCrop} />
                            } />
                            <Route path="/productividad" element={
                                <ProductivityDashboard crop={selectedCrop} />
                            } />
                            <Route path="/finanzas" element={
                                <FinanceDashboard crop={selectedCrop} />
                            } />
                        </Routes>
                    )}
                </div>
            </main>

            {/* Chat Button */}
            <button
                className="chat-fab"
                onClick={() => setShowChat(!showChat)}
            >
                <MessageSquare size={24} />
            </button>

            {/* Chat Window */}
            {showChat && (
                <ChatBot
                    crop={selectedCrop}
                    onClose={() => setShowChat(false)}
                />
            )}
        </div>
    )
}

// Dashboard Overview Component with Dynamic Data
function DashboardOverview({ crop }) {
    const [data, setData] = useState(null)

    useEffect(() => {
        if (crop) {
            const allData = generateAllData(crop)
            setData(allData)
        }
    }, [crop])

    if (!data) return <div className="loading">Cargando datos...</div>

    const { soil, environment, productivity, finance, alerts, config } = data
    const currentSoil = soil.current[0] // First zone
    const envCurrent = environment.current.current

    return (
        <div className="dashboard-overview">
            {/* CTA Banner */}
            <div className="cta-banner glass-card">
                <div className="cta-content">
                    <div className="cta-icon">🌱</div>
                    <div className="cta-text">
                        <h3>¿Te interesa TerraMind para tu finca?</h3>
                        <p>Agenda una demo personalizada con nuestro equipo comercial</p>
                    </div>
                </div>
                <div className="cta-actions">
                    <a href="mailto:comercial@saineco.com" className="cta-btn secondary">
                        <Mail size={18} />
                        comercial@saineco.com
                    </a>
                    <a href="https://ia.saineco.com/contacto/" target="_blank" rel="noopener noreferrer" className="cta-btn primary">
                        Contáctanos
                        <ExternalLink size={16} />
                    </a>
                </div>
            </div>

            <div className="overview-header">
                <h2>{config.emoji} Resumen General - {crop}</h2>
                <p>Vista consolidada de todos los módulos • Datos actualizados al {new Date().toLocaleDateString('es-CO')}</p>
            </div>

            <div className="overview-grid">
                <div className="overview-card glass-card soil-card">
                    <div className="card-header">
                        <BarChart3 size={24} />
                        <h3>Estado del Suelo</h3>
                    </div>
                    <div className="card-metrics">
                        <div className="metric">
                            <span className="metric-value">{currentSoil.ph}</span>
                            <span className="metric-label">pH</span>
                            <span className={`metric-status ${currentSoil.ph >= config.ranges.ph.min && currentSoil.ph <= config.ranges.ph.max ? 'good' : 'warning'}`}>
                                {currentSoil.ph >= config.ranges.ph.min && currentSoil.ph <= config.ranges.ph.max ? 'Óptimo' : 'Revisar'}
                            </span>
                        </div>
                        <div className="metric">
                            <span className="metric-value">{currentSoil.humidity}%</span>
                            <span className="metric-label">Humedad</span>
                            <span className="metric-status good">Óptimo</span>
                        </div>
                        <div className="metric">
                            <span className="metric-value">{Math.round(currentSoil.nitrogen)}</span>
                            <span className="metric-label">N (mg/kg)</span>
                            <span className={`metric-status ${currentSoil.nitrogen >= config.ranges.nitrogen.min ? 'good' : 'warning'}`}>
                                {currentSoil.nitrogen >= config.ranges.nitrogen.min ? 'Óptimo' : 'Bajo'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="overview-card glass-card environment-card">
                    <div className="card-header">
                        <CloudRain size={24} />
                        <h3>Condiciones Ambientales</h3>
                    </div>
                    <div className="card-metrics">
                        <div className="metric">
                            <span className="metric-value">{envCurrent.airTemperature}°C</span>
                            <span className="metric-label">Temperatura</span>
                            <span className="metric-status good">Óptimo</span>
                        </div>
                        <div className="metric">
                            <span className="metric-value">{envCurrent.humidity}%</span>
                            <span className="metric-label">Humedad Rel.</span>
                            <span className="metric-status good">Óptimo</span>
                        </div>
                        <div className="metric">
                            <span className="metric-value">{envCurrent.precipitation}mm</span>
                            <span className="metric-label">Lluvia (24h)</span>
                            <span className="metric-status info">Normal</span>
                        </div>
                    </div>
                </div>

                <div className="overview-card glass-card productivity-card">
                    <div className="card-header">
                        <BarChart3 size={24} />
                        <h3>Productividad</h3>
                    </div>
                    <div className="card-metrics">
                        <div className="metric">
                            <span className="metric-value">{productivity.metrics.tonPerHa}</span>
                            <span className="metric-label">ton/ha</span>
                            <span className={`metric-status ${productivity.metrics.tonPerHa >= config.finance.yieldTonHa ? 'good' : 'info'}`}>
                                {productivity.metrics.tonPerHa >= config.finance.yieldTonHa ? '+Meta' : 'En rango'}
                            </span>
                        </div>
                        <div className="metric">
                            <span className="metric-value">{productivity.metrics.totalHarvested.toLocaleString()}</span>
                            <span className="metric-label">kg cosechados</span>
                            <span className="metric-status info">Este mes</span>
                        </div>
                    </div>
                </div>

                <div className="overview-card glass-card finance-card">
                    <div className="card-header">
                        <DollarSign size={24} />
                        <h3>Resumen Financiero</h3>
                    </div>
                    <div className="card-metrics">
                        <div className="metric">
                            <span className="metric-value">${(finance.summary.totalIncome / 1000000).toFixed(1)}M</span>
                            <span className="metric-label">Ingresos (mes)</span>
                            <span className="metric-status good">+{Math.floor(Math.random() * 10 + 5)}%</span>
                        </div>
                        <div className="metric">
                            <span className="metric-value">{finance.summary.grossMargin}%</span>
                            <span className="metric-label">Margen</span>
                            <span className={`metric-status ${parseFloat(finance.summary.grossMargin) > 20 ? 'good' : 'warning'}`}>
                                {parseFloat(finance.summary.grossMargin) > 20 ? 'Saludable' : 'Revisar'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="alerts-section">
                <h3>🔔 Alertas Recientes</h3>
                <div className="alerts-list">
                    {alerts.map((alert) => (
                        <div key={alert.id} className={`alert-item ${alert.type}`}>
                            <span className="alert-icon">
                                {alert.type === 'warning' ? '⚠️' : alert.type === 'success' ? '✅' : '🔔'}
                            </span>
                            <div className="alert-content">
                                <strong>{alert.title}</strong>
                                <p>{alert.description}</p>
                            </div>
                            <span className="alert-time">{alert.zone}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Dashboard
